/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { reducer, initialState } from '../../reducers/containerReducer';
import Details from '@mui/icons-material/PlaylistAdd';
import { useValidation, isValid } from '@totalsoft/pure-validations-react';
import { documentClauseValidator } from 'features/documentClause/validators';
import DocumentClauseDetailComponent from './DocumentClauseDetailComponent';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { FakeText, Card, IconButton } from '@totalsoft/rocket-ui';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { CLAUSE_TYPES_QUERY, PARENT_CLAUSES_QUERY, DOCUMENT_CLAUSE_QUERY } from './queries';
import { ACTIVE_DOCUMENT_TYPES } from '../../../automaticStateTransition/queries';
import { ADD_UPDATE_DOCUMENT_CLAUSE } from './mutations';
import { DOCUMENT_CLAUSE_UPDATED } from './subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const DocumentClauseContainer = ({ clauseId, onSaveFnChanged, onChangeDocumentClauseTitle, onRedirect }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [rendered, setRendered] = useState(false);
  const [documentClauseValidation, validateDocumentClause] = useValidation(documentClauseValidator);

  const { data: documentTypes, loading: documentTypesLoading } = useQuery(ACTIVE_DOCUMENT_TYPES);

  const {
    data,
    loading: loadingHeader,
    refetch
  } = useQuery(DOCUMENT_CLAUSE_QUERY, {
    variables: { clauseId: clauseId },
    skip: clauseId === undefined
  });

  const { data: clauseTypesData, loading: clauseTypesLoading, refetch: refetchClauseTypes } = useQuery(CLAUSE_TYPES_QUERY, {});

  const { data: parentClausesData, loading: parentClausesLoading, refetch: refetchParentClauses } = useQuery(PARENT_CLAUSES_QUERY);

  const [addOrUpdateDocumentClause] = useMutation(ADD_UPDATE_DOCUMENT_CLAUSE);

  useSubscription(DOCUMENT_CLAUSE_UPDATED, {
    onData: data => {
      addToast(t('Clause.ClauseUpdated'), 'success');
      dispatch({ type: 'OnItemSaved' });
      if (clauseId === undefined) onRedirect(data.data.data.documentClauseUpdated.clause.clauseId);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedDocumentClauses' });
      client.cache.gc();
    }
  });

  const onLanguageChange = useCallback(() => {
    refetch();
    refetchClauseTypes();
    refetchParentClauses();
  }, [refetch, refetchClauseTypes, refetchParentClauses]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (!clauseId) dispatch({ type: 'OnItemAdded' });
    else if (data && data.documentClause) dispatch({ type: 'OnInitState', clause: data.documentClause });
  }, [clauseId, data]);

  useEffect(() => {
    if (localState.item.clauseText) {
      onChangeDocumentClauseTitle(localState.item.clauseText);
    }
  }, [localState.item.clauseText, onChangeDocumentClauseTitle]);

  const onSave = useCallback(() => {
    if (!validateDocumentClause(localState.item)) return;
    const { changed, saving, ...clause } = localState.item;
    const input = { ...clause };
    dispatch({ type: 'OnItemSaving' });
    addOrUpdateDocumentClause({ variables: { input } });
  }, [addOrUpdateDocumentClause, localState.item, validateDocumentClause]);

  useEffect(() => {
    validateDocumentClause(localState.item);
  }, [localState.item, validateDocumentClause]);

  useEffect(() => {
    setRendered(true);
  }, [rendered]);

  useEffect(() => {
    if (!rendered) return;
    onSaveFnChanged(onSave, !localState.item.changed);
  }, [onSaveFnChanged, localState.item.changed, rendered, onSave]);

  const loading = clauseTypesLoading || parentClausesLoading || documentTypesLoading || loadingHeader || localState === initialState;
  return (
    <Card
      icon={Details}
      iconColor='primary'
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('Clause.Item.SaveChanges')}
          onClick={onSave}
          disabled={!localState.item.changed || localState.item.saving || !(documentClauseValidation |> isValid)}
        />
      }
    >
      {loading ? (
        <FakeText lines={10} />
      ) : (
        <DocumentClauseDetailComponent
          clause={localState}
          clauseTypes={clauseTypesData ? clauseTypesData.clauseTypes : []}
          documentTypes={documentTypes ? documentTypes.activeDocumentTypes : []}
          parentClauses={parentClausesData ? parentClausesData.parentClauses : []}
          dispatch={dispatch}
          validation={documentClauseValidation}
          onSave={onSave}
        />
      )}
    </Card>
  );
};

DocumentClauseContainer.propTypes = {
  clauseId: PropTypes.number,
  onSaveFnChanged: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired,
  onChangeDocumentClauseTitle: PropTypes.func
};

export default DocumentClauseContainer;
